import { useState, useCallback, useMemo } from "react";
import BreakdownDialog from "./BreakdownDialog";
import { assetTypeValues } from "../../../constants";

export const LESSEE_REVENUE_BREAKDOWN_COLUMNS = [
  { key: "lessee", label: "Lessee" },
  { key: "lease_ref_code", label: "Lease Ref Code" },
  { key: "asset_label", label: "Asset" },
  { key: "revenue", label: "Revenue", mono: true },
  { key: "share_pct", label: "Share %", mono: true },
];

function formatAmount(value, currency) {
  const amount = typeof value === "number" ? value : parseFloat(value);
  if (Number.isNaN(amount)) return "-";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return amount.toFixed(2);
  }
}

function formatPercent(value) {
  const pct = typeof value === "number" ? value : parseFloat(value);
  return Number.isFinite(pct) ? `${pct.toFixed(2)}%` : "-";
}

function buildLesseeRevenueRows(items, currency) {
  if (!Array.isArray(items)) return [];
  return items.map((item, index) => ({
    id: `${item?.lessee || "lessee"}-${item?.lease_ref_code || "lease"}-${index}`,
    lessee: item?.lessee || "-",
    lease_ref_code: item?.lease_ref_code || "-",
    asset_label: [assetTypeValues[item?.asset_type]?.label, item?.asset_sn].filter(Boolean).join(" ") || "-",
    revenue: formatAmount(item?.revenue, currency),
    share_pct: formatPercent(item?.share_pct),
  }));
}

// The panel has no per-lessee marks, so clicking it opens the full lessee_revenue_breakdown.
export function useLesseeRevenueConcentrationBreakdown({ revenueBreakdown, currency, onTrack }) {
  const [modalOpen, setModalOpen] = useState(false);
  const reset = useCallback(() => setModalOpen(false), []);

  const rows = useMemo(() => buildLesseeRevenueRows(revenueBreakdown, currency), [revenueBreakdown, currency]);

  const onPanelClick = useCallback(() => {
    setModalOpen(true);
    onTrack?.("Graph Panel Clicked: Lessee Revenue Concentration");
  }, [onTrack]);

  return { rows, canRender: rows.length > 0, onPanelClick, modalOpen, closeModal: reset, reset };
}

export function LesseeRevenueConcentrationBreakdownModal({ open, onClose, rows }) {
  const title = "Lessee Revenue Concentration Index";
  const rowCount = Array.isArray(rows) ? rows.length : 0;
  const subtitle = `${rowCount} ${rowCount === 1 ? "Lease" : "Leases"}`;
  return (
    <BreakdownDialog
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      exportFileName={title}
      columns={LESSEE_REVENUE_BREAKDOWN_COLUMNS}
      rows={rows}
    />
  );
}
